import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, getTranslation } from '@resy/ui-kitchen';
import CheckboxSimple from '../../../../../ui-kitchen/src/components/Checkbox/CheckboxSimple';
import Close from '../../../../../ui-kitchen/src/svg/icons/ic_close_20x20.svg';
import ResyLogo from '../../../images/resy-logo-red.svg';
import TockLogo from '../../../../../ui-kitchen/src/svg/logos/tock-logo.svg';
import Logger from '../../../../../ui-kitchen/src/utils/logger';
import { postOptimizelyTrackEvents } from '../../../../../ui-kitchen/src/api';
import ABTestWithFallback from '../../../../../ui-kitchen/src/components/Experiments/ABTestWithFallback';
import Variation from '../../../../../ui-kitchen/src/components/Experiments/Variation';
import { HIDE_TOCK_INTERSTITIAL } from '../../../../../ui-kitchen/src/reducers/tockInterstitialSlice';

import './TockInterstitial.scss';

const getInitialHidePreference = () => {
  if (typeof window === 'undefined') return false;

  try {
    return window.localStorage.getItem(HIDE_TOCK_INTERSTITIAL) === 'true';
  } catch {
    return false;
  }
};

const TockInterstitial = ({ Analytics, onConfirm, onClose }) => {
  const [hideInterstitial, setHideInterstitial] = useState(getInitialHidePreference);
  const trackToggleOn = (variant) => {
    Promise.resolve(postOptimizelyTrackEvents({
      slug: 'toggle_on_tock_modal',
      data: {
        track_user_id: true,
        userAttributes: {
          platform: 'web',
        },
      },
    })).catch((err) => Logger.error('[OptimizelyTrackEvent] Error:', err));
    Analytics?.event?.('Toggle On Tock Modal', { variant });
  };

  const handlePreferenceChange = (event) => {
    const { checked } = event.target;
    setHideInterstitial(checked);

    try {
      window.localStorage.setItem(HIDE_TOCK_INTERSTITIAL, checked ? 'true' : 'false');
    } catch {
      Logger.log('TockInterstitial: failed to persist hide preference');
    }
  };
  const EXP_FLAG_KEY = 'tock_bridge_transition_copy';
  const EXP_FALLBACK_KEY = 'control';
  const EXP_VARIATION_KEY = 'feature';
  const EXP_VARIATION_KEY_LIST = [EXP_FALLBACK_KEY, EXP_VARIATION_KEY];

  const trackCtaClick = (variant) => {
    Promise.resolve(postOptimizelyTrackEvents({
      slug: 'tock_bridge_cta_clicked',
      data: {
        track_user_id: true,
        userAttributes: {
          platform: 'web',
        },
      },
    })).catch((err) => Logger.error('[OptimizelyTrackEvent] Error:', err));

    Analytics?.event?.('Tock Bridge CTA Clicked', { variant });
  };

  const handleConfirmClick = (variant) => {
    trackCtaClick(variant);
    if (hideInterstitial) {
      trackToggleOn(variant);
    }
    onConfirm();
  };

  return (
    <div className="TockInterstitial" data-test-id="tock-interstitial">
      <Button
        onClick={onClose}
        variant="no-style"
        className="TockInterstitial__Close"
        data-test-id="tock-interstitial-button-close"
        aria-label={getTranslation({ translationKey: 'close', fallback: 'Close' })}
      >
        <Close />
      </Button>
      <div className="TockInterstitial__Header">
        <p className="TockInterstitial__Subheader">
          {getTranslation({
            translationKey: 'tock_interstitial_subheader',
            fallback: 'Tock is now part of the Resy Network',
          })}
        </p>
        <div className="TockInterstitial__Logos" aria-hidden="true">
          <ResyLogo className="TockInterstitial__Logo TockInterstitial__Logo--resy" />
          <TockLogo className="TockInterstitial__Logo TockInterstitial__Logo--tock" />
        </div>
      </div>
      <div className="TockInterstitial__Body">
        <ABTestWithFallback
          flagKey={EXP_FLAG_KEY}
          fallbackKey={EXP_FALLBACK_KEY}
          variationKeyList={EXP_VARIATION_KEY_LIST}
        >
          <Variation variationKey={EXP_FALLBACK_KEY}>
            <h2 className="TockInterstitial__Title">
              {getTranslation({
                translationKey: 'tock_interstitial_header',
                fallback: 'Finish on Tock',
              })}
            </h2>
            <p className="TockInterstitial__Description">
              {getTranslation({
                translationKey: 'tock_interstitial_description',
                fallback: "This restaurant is managed by Tock. You'll be redirected to Tock to continue your experience.",
              })}
            </p>
          </Variation>
          <Variation variationKey={EXP_VARIATION_KEY}>
            <h2 className="TockInterstitial__Title">
              {getTranslation({
                translationKey: 'tock_interstitial_header_variation',
                fallback: "You're leaving Resy",
              })}
            </h2>
            <p className="TockInterstitial__Description">
              {getTranslation({
                translationKey: 'tock_interstitial_description_variation',
                fallback: "You'll be redirected to Tock, where this restaurant is managed, to continue your experience.",
              })}
            </p>
          </Variation>
        </ABTestWithFallback>
      </div>
      <div className="TockInterstitial__Footer">
        <div className="TockInterstitial__Preference">
          <CheckboxSimple
            name="tock-interstitial-hide"
            checked={hideInterstitial}
            onChange={handlePreferenceChange}
          />
          <label htmlFor="tock-interstitial-hide" className="TockInterstitial__PreferenceLabel">
            {getTranslation({
              translationKey: 'tock_interstitial_preference_hide',
              fallback: "Don't show this again",
            })}
          </label>
        </div>
        <ABTestWithFallback
          flagKey={EXP_FLAG_KEY}
          fallbackKey={EXP_FALLBACK_KEY}
          variationKeyList={EXP_VARIATION_KEY_LIST}
        >
          <Variation variationKey={EXP_FALLBACK_KEY}>
            <Button
              className="TockInterstitial__CTA"
              variant="primary"
              onClick={() => handleConfirmClick(EXP_FALLBACK_KEY)}
              data-test-id="tock-interstitial-button-confirm"
            >
              {getTranslation({
                translationKey: 'tock_interstitial_confirm_cta',
                fallback: 'Continue to Tock',
              })}
            </Button>
          </Variation>
          <Variation variationKey={EXP_VARIATION_KEY}>
            <Button
              className="TockInterstitial__CTA"
              variant="primary"
              onClick={() => handleConfirmClick(EXP_VARIATION_KEY)}
              data-test-id="tock-interstitial-button-confirm"
            >
              {getTranslation({
                translationKey: 'tock_interstitial_confirm_cta_variation',
                fallback: 'Continue',
              })}
            </Button>
          </Variation>
        </ABTestWithFallback>
      </div>
    </div>
  );
};

TockInterstitial.displayName = 'TockInterstitial';

TockInterstitial.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }),
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

TockInterstitial.defaultProps = {
  Analytics: undefined,
};

export default TockInterstitial;
